import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, UserCog, KeyRound, AtSign, ChevronDown } from "lucide-react";

const GENDERS = [["homme", "Homme"], ["femme", "Femme"], ["autre", "Autre"]];

function Section({ icon: Icon, title, testid, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#141C17] overflow-hidden">
      <button data-testid={testid} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-lime-400" />
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <ChevronDown className={`h-4 w-4 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
}

const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-lime-400/50";

export default function AccountSettings() {
  const { checkAuth } = useAuth();
  const [prof, setProf] = useState({ date_of_birth: "", gender: "", height_cm: "", weight_kg: "", age: null });
  const [initial, setInitial] = useState({ height_cm: "", weight_kg: "" });
  const [busy, setBusy] = useState("");
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    apiClient.get("/profile").then(({ data }) => {
      setProf({
        date_of_birth: data.date_of_birth || "", gender: data.gender || "",
        height_cm: data.height_cm || "", weight_kg: data.weight_kg || "", age: data.age,
      });
      setInitial({ height_cm: data.height_cm || "", weight_kg: data.weight_kg || "" });
    }).catch(() => {});
  }, []);

  const setP = (k, v) => setProf((p) => ({ ...p, [k]: v }));

  const saveProfile = async () => {
    setBusy("profile");
    try {
      const payload = {
        date_of_birth: prof.date_of_birth || null,
        gender: prof.gender || null,
        height_cm: prof.height_cm ? parseFloat(prof.height_cm) : null,
        weight_kg: prof.weight_kg ? parseFloat(prof.weight_kg) : null,
      };
      await apiClient.patch("/profile", payload);
      await checkAuth();
      toast.success("Profil mis à jour.");
      const whChanged = String(prof.weight_kg) !== String(initial.weight_kg) || String(prof.height_cm) !== String(initial.height_cm);
      if (whChanged) {
        toast.message("Les changements de poids et de taille seront pris en compte au prochain bilan mensuel.");
        setInitial({ height_cm: prof.height_cm, weight_kg: prof.weight_kg });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement.");
    } finally { setBusy(""); }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 6) { toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères."); return; }
    if (pw.new_password !== pw.confirm) { toast.error("Les deux mots de passe ne correspondent pas."); return; }
    setBusy("password");
    try {
      const { data } = await apiClient.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      if (data?.token) localStorage.setItem("pousse_token", data.token);
      toast.success("Mot de passe modifié.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur.");
    } finally { setBusy(""); }
  };

  const changeEmail = async () => {
    if (!newEmail) { toast.error("Indique la nouvelle adresse."); return; }
    setBusy("email");
    try {
      await apiClient.post("/auth/change-email", { new_email: newEmail });
      toast.success("Email de confirmation envoyé à la nouvelle adresse. Clique sur le lien pour valider.");
      setNewEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur.");
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-3">
      <Section icon={UserCog} title="Mon profil" testid="settings-profile-toggle">
        <div>
          <label className="text-xs text-white/50 ml-1">Date de naissance {prof.age ? `(${prof.age} ans)` : ""}</label>
          <input data-testid="profile-dob-input" type="date" value={prof.date_of_birth} onChange={(e) => setP("date_of_birth", e.target.value)} className={inputCls + " mt-1"} />
        </div>
        <div>
          <label className="text-xs text-white/50 ml-1">Sexe</label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {GENDERS.map(([k, l]) => (
              <button key={k} data-testid={`profile-gender-${k}`} onClick={() => setP("gender", k)}
                className={`rounded-xl py-2.5 text-sm font-semibold border transition-all ${prof.gender === k ? "border-lime-400 bg-lime-400/10 text-lime-300" : "border-white/10 text-white/60"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-white/50 ml-1">Taille (cm)</label>
            <input data-testid="profile-height-input" type="number" value={prof.height_cm} onChange={(e) => setP("height_cm", e.target.value)} className={inputCls + " mt-1"} />
          </div>
          <div>
            <label className="text-xs text-white/50 ml-1">Poids (kg)</label>
            <input data-testid="profile-weight-input" type="number" value={prof.weight_kg} onChange={(e) => setP("weight_kg", e.target.value)} className={inputCls + " mt-1"} />
          </div>
        </div>
        <button data-testid="save-profile-button" onClick={saveProfile} disabled={busy === "profile"}
          className="w-full inline-flex items-center justify-center gap-2 bg-lime-400 text-black font-extrabold py-3 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all disabled:opacity-60">
          {busy === "profile" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
        </button>
      </Section>

      <Section icon={KeyRound} title="Mot de passe" testid="settings-password-toggle">
        <input data-testid="current-password-input" type="password" placeholder="Mot de passe actuel" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} className={inputCls} />
        <input data-testid="new-password-input" type="password" placeholder="Nouveau mot de passe" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} className={inputCls} />
        <input data-testid="confirm-password-input" type="password" placeholder="Confirme le nouveau mot de passe" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} className={inputCls} />
        <button data-testid="change-password-button" onClick={changePassword} disabled={busy === "password"}
          className="w-full inline-flex items-center justify-center gap-2 bg-lime-400 text-black font-extrabold py-3 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all disabled:opacity-60">
          {busy === "password" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Changer mon mot de passe"}
        </button>
      </Section>

      <Section icon={AtSign} title="Adresse email" testid="settings-email-toggle">
        <input data-testid="new-email-input" type="email" placeholder="Nouvelle adresse email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={inputCls} />
        <p className="text-xs text-white/40 px-1">Un email de confirmation sera envoyé à la nouvelle adresse. Le changement ne sera validé qu'après le clic sur le lien.</p>
        <button data-testid="change-email-button" onClick={changeEmail} disabled={busy === "email"}
          className="w-full inline-flex items-center justify-center gap-2 bg-lime-400 text-black font-extrabold py-3 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all disabled:opacity-60">
          {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer le lien de confirmation"}
        </button>
      </Section>
    </div>
  );
}
